import { difference, get, isEmpty, omit, set, toPath } from 'lodash-es';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button, { ButtonContainer, SaveLoadingButton } from '../form/Button';
import {
    ConfigHelper,
    DisabledConfigHelper,
    useConfigHelperWithReactFormHook,
} from '../pages/measuringPoint/configHelper';
import { PERMISSION } from '../pages/sharing/utils';
import { ErrorToNotificationsLink } from './ErrorToNotificationsLink';
import { extractEnabledFields, FormContext, OVERRIDES_NAME, useWatchNestedField } from './utils';
import { resolver } from './validation';

const Form = ({
    projectIgnoreFields,
    information,
    defaultValues,
    InjectableComponent,
    DataLink,
    dataLinkProps,
    configHelper = new DisabledConfigHelper(),
    showSaveButton = true,
    saveIsInProgress,
    children,
}) => {
    const [onSubmitFunction, setOnSubmitFunction] = useState();

    const [values, setValues] = useState({});

    const reactHookForm = useForm({
        mode: 'all',
        defaultValues,
        resolver,
        context: {
            configHelper,
        },
    });

    const { handleSubmit, setError, setValue, getValues, watch, trigger, reset } = reactHookForm;

    const overriddenFields = useWatchNestedField(watch, OVERRIDES_NAME, {});

    const overriddenValuesBackup = useRef(new Map());

    const setOverriddenField = useCallback(
        (field, value, removeFromBackup) => {
            setValue(`${OVERRIDES_NAME}.${field}`, value);

            // `removeFromBackup` is used by the `ChoiceField` to ensure that once a user selects
            // a non-project option, the selected option does not revert to the value that was set
            // before the project was chosen. This essentially prevents the behavior described below
            // in `Revert field values`.
            if (removeFromBackup) {
                overriddenValuesBackup.current.has(field) &&
                    overriddenValuesBackup.current.delete(field);
            }
        },
        [setValue]
    );

    const { setServerValuesUpdate } = dataLinkProps;

    useEffect(() => {
        if (isEmpty(values)) {
            return;
        }

        // Set a flag to indicate that the upcoming form changes are being made programmatically,
        // not by the user. The configuration warning logic uses this flag to decide whether to
        // suppress popups or to apply auto-fixes for these server-driven updates.
        setServerValuesUpdate && setServerValuesUpdate(true);

        // Use the `reset` function to populate the form with new values. This is the standard
        // method recommended by React Hook Form (RHF) for this purpose.
        // See: https://github.com/react-hook-form/react-hook-form/blob/d8bb9b316a64840210b9f0611fdd8c74719f9920/src/useForm.ts#L156
        reset(omit(values, OVERRIDES_NAME));
        // Override needs to be assigned to a sub element.
        if (values[OVERRIDES_NAME]) {
            Object.entries(values[OVERRIDES_NAME]).forEach(([name, value]) =>
                setOverriddenField(name, value, false)
            );
        }

        // Unset the flag to allow the configuration warning logic to react to subsequent
        // user-driven changes.
        setServerValuesUpdate && setServerValuesUpdate(false);

        // Manually trigger form validation after resetting with new server values.
        // This ensures that the newly populated form is immediately validated.
        // A built-in option for this in RHF's `reset` function was not apparent at the time of
        // writing.
        trigger();
    }, [
        values,
        reset,
        dataLinkProps,
        trigger,
        setServerValuesUpdate,
        setValue,
        setOverriddenField,
    ]);

    const [parentValues, setParentValues] = useState(null);
    const [loading, setLoading] = useState(false);

    const mergedDataLinkProps = useMemo(
        () => ({
            ...dataLinkProps,
            setOnSubmitFunction,
            setError,
            setLoading,
            setValues,
            setParentValues,
        }),
        [dataLinkProps, setOnSubmitFunction, setError, setLoading, setValues, setParentValues]
    );

    useEffect(() => {
        function setNestedValue(name, sourceValues) {
            const rootName = toPath(name)[0];
            const isNested = name !== rootName;
            const value = get(sourceValues, name);

            // If this field is a nested field, merge the value under the nested path
            // onto the existing values and update the root field for the nested path
            // with the merged/updated root value. This is necessary because React Hook Form's
            // `setValue` does not support setting nested fields for fields that aren't
            // registered as nested fields. An example of such fields is
            // `notificationConfig.emailDelay`, which is part of a field originally
            // registered as `notificationConfig`.
            if (isNested) {
                const newValues = set(getValues(), name, value);
                const newRootValue = get(newValues, rootName);
                setValue(rootName, newRootValue);
            } else {
                // Set the field value to the project field value.
                setValue(name, value);
            }
        }

        // These are the fields we do NOT want to grab from the project.
        const removeParentFields = [...extractEnabledFields(overriddenFields), 'name', 'id'];

        // This is the remaining project data that needs to be loaded into the form.
        const newParentValues = omit(parentValues, removeParentFields);

        const newParentFields = extractEnabledFields(newParentValues);

        // `overriddenValuesBackup` contains a backup of the field values from before the
        // project values were merged over them. If a field is inside the
        // `overriddenValuesBackup` but no longer in the `newParentValues`, it means that
        // someone either:
        // a) Clicked the `override` checkbox for that field.
        // b) Detached the measuring point from a project (set project to null).
        // In both cases, we need to revert the form value to its state before the project
        // values were merged back into the form.
        const toBeReverted = difference(
            extractEnabledFields(overriddenValuesBackup.current),
            newParentFields
        );

        // Revert field values for fields that were overridden.
        toBeReverted.forEach((name) => {
            setNestedValue(name, Object.fromEntries(overriddenValuesBackup.current.entries()));

            overriddenValuesBackup.current.delete(name);
        });

        // Here we are going to override the form with the new project values.
        newParentFields.forEach((name) => {
            // Store a backup of the 'before project' field value.
            if (!overriddenValuesBackup.current.has(name)) {
                overriddenValuesBackup.current.set(name, getValues(name));
            }

            setNestedValue(name, parentValues);
        });

        // Trigger validation.
        trigger();
    }, [parentValues, setValue, overriddenFields, getValues, trigger]);

    const [showAreYouSureToOverrideMessage, setShowAreYouSureToOverrideMessage] = useState(true);

    const askAreYouSureToOverrideMessageOnce = useCallback(() => {
        const message = gettext('Are you sure you want to override the project settings?');
        if (showAreYouSureToOverrideMessage) {
            // eslint-disable-next-line no-restricted-globals, no-alert
            if (!confirm(message)) {
                return false;
            }

            setShowAreYouSureToOverrideMessage(false);
        }

        return true;
    }, [showAreYouSureToOverrideMessage]);

    useConfigHelperWithReactFormHook(configHelper, reactHookForm);

    const triggerSubmit = useCallback(
        (event) => {
            // Editing requires `FULL_ACCESS`.
            if ('id' in values && values.permission !== PERMISSION.FULL_ACCESS) {
                event.preventDefault();
                return;
            }

            handleSubmit(onSubmitFunction)(event);
        },
        [onSubmitFunction, handleSubmit, values]
    );

    const context = useMemo(
        () => ({
            parentValues,
            setOverriddenField,
            projectIgnoreFields,
            information,
            askAreYouSureToOverrideMessageOnce,
            configHelper,
            triggerSubmit,
        }),
        [
            parentValues,
            setOverriddenField,
            projectIgnoreFields,
            information,
            askAreYouSureToOverrideMessageOnce,
            configHelper,
            triggerSubmit,
        ]
    );

    return (
        <FormProvider {...reactHookForm}>
            <FormContext.Provider value={context}>
                <ErrorToNotificationsLink configHelper={configHelper} />
                {DataLink && <DataLink {...mergedDataLinkProps} />}
                {loading && (
                    <div className="main-container-new">
                        <p>{gettext('LOADING')}</p>
                    </div>
                )}
                {!loading && (
                    <form onSubmit={triggerSubmit}>
                        {children}

                        {showSaveButton && (
                            <div className="even:bg-neutral-200">
                                <div className="main-container-new py-8">
                                    <ButtonContainer>
                                        <SaveLoadingButton saveIsInProgress={saveIsInProgress} />
                                        <Button variant="secondary" href="/measuring_point/">
                                            {gettext('CANCEL')}
                                        </Button>
                                    </ButtonContainer>
                                </div>
                            </div>
                        )}
                    </form>
                )}
                {InjectableComponent && <InjectableComponent />}
            </FormContext.Provider>
        </FormProvider>
    );
};

Form.propTypes = {
    children: PropTypes.node.isRequired,
    configHelper: PropTypes.instanceOf(ConfigHelper),
    DataLink: PropTypes.func,
    dataLinkProps: PropTypes.shape({
        queries: PropTypes.object,
        pk: PropTypes.number,
        onSubmitSuccessful: PropTypes.func,
        convertDataIntoFormValues: PropTypes.func,
        setServerValuesUpdate: PropTypes.func,
    }),
    InjectableComponent: PropTypes.func,
    defaultValues: PropTypes.object,
    information: PropTypes.object,
    projectIgnoreFields: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)])
    ),
    showSaveButton: PropTypes.bool,
    saveIsInProgress: PropTypes.bool,
};

export default Form;
